import { Bot, Code2, Globe, ArrowUpRight, Check } from "lucide-react";
import { useLeadModal } from "../../context/LeadModalContext";

export default function Services() {
    const { openModal } = useLeadModal();

    return (
        <section
            id="services"
            data-testid="services-section"
            className="relative py-24 sm:py-32 scroll-mt-nav"
        >
            <div className="absolute top-1/2 left-0 w-1/2 h-1/2 -translate-y-1/2 bg-[#1d4ed8]/10 blur-[120px] pointer-events-none" />

            <div className="relative max-w-7xl mx-auto px-6 sm:px-10 lg:px-16">
                <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
                    <div className="max-w-2xl">
                        <span className="text-[11px] font-bold uppercase tracking-[0.25em] text-[#00f0ff]">
                            What we build
                        </span>
                        <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tighter mt-4 leading-[1.05]">
                            AI development,{" "}
                            <span className="text-gradient-cyan">delivered weekly</span>.
                        </h2>
                    </div>
                    <p className="text-[#94a3b8] md:max-w-sm">
                        Three services. One mission: get production-grade AI into your business
                        before your competitors finish their slide deck.
                    </p>
                </div>

                {/* Bento grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 grid-rows-[auto] gap-5">
                    {/* Custom AI Agents – spans 2 cols and 2 rows on desktop */}
                    <article
                        data-testid="service-agents"
                        className="relative md:col-span-2 lg:row-span-2 group rounded-2xl bg-[#0a1325] border border-white/10 hover:border-[#00f0ff]/40 overflow-hidden transition-all duration-500 hover:-translate-y-1"
                    >
                        <div
                            className="absolute inset-0 opacity-30 mix-blend-screen pointer-events-none"
                            style={{
                                backgroundImage:
                                    "url('https://images.unsplash.com/photo-1751170958511-e9df5cdf9a47?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHwxfHxhcnRpZmljaWFsJTIwaW50ZWxsaWdlbmNlJTIwYmx1ZXxlbnwwfHx8fDE3NzgxNzU0NDV8MA&ixlib=rb-4.1.0&q=85')",
                                backgroundSize: "cover",
                                backgroundPosition: "right center",
                                maskImage:
                                    "linear-gradient(to top, rgba(0,0,0,0.7), transparent 60%)",
                                WebkitMaskImage:
                                    "linear-gradient(to top, rgba(0,0,0,0.7), transparent 60%)",
                            }}
                        />
                        <div className="relative p-8 sm:p-12 flex flex-col h-full min-h-[460px]">
                            <div className="flex items-center justify-between mb-8">
                                <div className="w-12 h-12 rounded-lg bg-[#00f0ff]/15 border border-[#00f0ff]/40 flex items-center justify-center">
                                    <Bot className="w-6 h-6 text-[#00f0ff]" />
                                </div>
                                <span className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#00f0ff]/80">
                                    Flagship
                                </span>
                            </div>

                            <h3 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight mb-4 leading-tight">
                                Custom AI Agents
                            </h3>
                            <p className="text-[#94a3b8] leading-relaxed max-w-xl mb-8">
                                Support agents that close tickets while you sleep. Sales agents
                                that qualify leads and book calls. Ops agents that read your
                                emails and update your CRM. Built bespoke, trained on your data,
                                connected to the tools you already pay for.
                            </p>

                            <ul className="space-y-2.5 mb-10">
                                {[
                                    "GPT-5 / Claude / Gemini routing for cost-optimized intelligence",
                                    "Slack, Email, Web Chat, WhatsApp, Voice — pick any channel",
                                    "Native integrations: Stripe, HubSpot, Notion, Shopify, Zapier",
                                    "Full conversation logs, analytics, and human-in-the-loop fallback",
                                ].map((feat) => (
                                    <li
                                        key={feat}
                                        className="flex items-start gap-2.5 text-sm text-[#cbd5e1]"
                                    >
                                        <Check className="w-3.5 h-3.5 text-[#00f0ff] mt-0.5 shrink-0" />
                                        <span>{feat}</span>
                                    </li>
                                ))}
                            </ul>

                            <button
                                onClick={() => openModal("service-agents")}
                                data-testid="service-agents-cta"
                                className="mt-auto self-start inline-flex items-center gap-2 px-5 py-2.5 rounded-lg border border-white/15 text-white hover:border-[#00f0ff] hover:text-[#00f0ff] transition-all duration-300"
                            >
                                Build my agent
                                <ArrowUpRight className="w-4 h-4" />
                            </button>
                        </div>
                    </article>

                    <ServiceCard
                        Icon={Code2}
                        title="Web Apps"
                        desc="Internal tools, dashboards, customer portals — full-stack and built around the agent that powers them. React, Next, FastAPI, Postgres."
                        bullets={["Auth, billing, multi-tenant", "Edge-ready deploy"]}
                        cta="Scope a build"
                        testId="service-webapps"
                    />

                    <ServiceCard
                        Icon={Globe}
                        title="AI Landing Pages"
                        desc="Conversion-focused pages with embedded chat, lead-qualifying agents, and instant demos. Designed to turn visitors into customers."
                        bullets={["A/B-tested copy", "Built-in analytics"]}
                        cta="See examples"
                        testId="service-landing"
                    />

                    {/* Wide bottom card */}
                    <article
                        data-testid="service-aaas"
                        className="relative md:col-span-2 lg:col-span-3 group rounded-2xl bg-gradient-to-br from-[#0a1325] via-[#0a1325] to-[#1d4ed8]/15 border border-[#00f0ff]/20 overflow-hidden transition-all duration-500 hover:border-[#00f0ff]/40"
                    >
                        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 p-8 sm:p-12 items-center">
                            <div className="md:col-span-7">
                                <span className="text-[11px] font-bold uppercase tracking-[0.25em] text-[#00f0ff]">
                                    Agent-as-a-Service
                                </span>
                                <h3 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight mt-3 mb-4 leading-tight">
                                    Your in-house AI team — without the headcount.
                                </h3>
                                <p className="text-[#94a3b8] leading-relaxed max-w-xl">
                                    Monthly retainer. Unlimited iterations on your agent.
                                    Add features, swap models, retrain on new data — same flat fee.
                                    Pause or cancel any month. We act as your fractional AI team.
                                </p>
                            </div>
                            <div className="md:col-span-5 flex flex-col gap-3">
                                <Stat label="Avg. ticket deflection" value="62%" />
                                <Stat label="Hours saved / month" value="180+" />
                                <Stat label="Time to first agent" value="4 days" />
                                <a
                                    href="/pricing"
                                    data-testid="service-aaas-cta"
                                    className="mt-2 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-lg bg-[#00f0ff] text-[#050b14] font-bold hover:bg-white transition-colors duration-300"
                                >
                                    See retainer plans
                                    <ArrowUpRight className="w-4 h-4" />
                                </a>
                            </div>
                        </div>
                    </article>
                </div>
            </div>
        </section>
    );
}

function ServiceCard({ Icon, title, desc, bullets, cta, testId }) {
    const { openModal } = useLeadModal();
    return (
        <article
            data-testid={testId}
            className="group relative rounded-2xl bg-[#0a1325] border border-white/10 hover:border-[#00f0ff]/40 p-8 transition-all duration-500 hover:-translate-y-1 overflow-hidden"
        >
            <div className="absolute -top-12 -right-12 w-40 h-40 rounded-full bg-[#00f0ff]/0 group-hover:bg-[#00f0ff]/10 blur-3xl transition-all duration-500" />
            <div className="relative">
                <div className="w-11 h-11 rounded-lg bg-[#00f0ff]/10 border border-[#00f0ff]/30 flex items-center justify-center mb-6">
                    <Icon className="w-5 h-5 text-[#00f0ff]" />
                </div>
                <h3 className="font-heading text-2xl font-bold tracking-tight mb-3">
                    {title}
                </h3>
                <p className="text-sm text-[#94a3b8] leading-relaxed mb-5">{desc}</p>
                <ul className="space-y-2 mb-6">
                    {bullets.map((b) => (
                        <li key={b} className="flex items-center gap-2 text-xs text-[#cbd5e1]">
                            <span className="w-1 h-1 rounded-full bg-[#00f0ff]" />
                            {b}
                        </li>
                    ))}
                </ul>
                <button
                    onClick={() => openModal(testId)}
                    className="inline-flex items-center gap-1.5 text-sm text-white hover:text-[#00f0ff] transition-colors"
                >
                    {cta}
                    <ArrowUpRight className="w-3.5 h-3.5" />
                </button>
            </div>
        </article>
    );
}

function Stat({ label, value }) {
    return (
        <div className="flex items-baseline justify-between gap-4 px-4 py-3 rounded-lg bg-[#050b14]/60 border border-white/10">
            <span className="text-xs uppercase tracking-wider text-[#94a3b8]">{label}</span>
            <span className="font-heading text-2xl font-bold text-white">{value}</span>
        </div>
    );
}
